import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Gate({ onSuccess }) {
  const { unlockInspection } = useAuth();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try { await unlockInspection(pin); onSuccess(); }
    catch (e) { setErr(e?.response?.data?.detail || "Invalid PIN"); }
    finally { setBusy(false); }
  };

  return (
    <div className="gate" data-testid="inspection-gate">
      <form className="gate-card" onSubmit={submit}>
        <div className="gate-mark">&#9873;</div>
        <h2>Restricted Layer</h2>
        <p>This view contains confidential employment classifications and inspection-vulnerability analysis.</p>
        <input type="password" maxLength={6} placeholder="&#8226;&#8226;&#8226;&#8226;" autoComplete="off" value={pin} onChange={e => setPin(e.target.value)} data-testid="inspection-pin-input"/>
        <div className="gate-error">{err}</div>
        <button type="submit" disabled={busy || !pin} data-testid="inspection-pin-submit">{busy ? "Verifying..." : "Authenticate"}</button>
        <div className="gate-hint">PIN required</div>
      </form>
    </div>
  );
}

const TYPE_COLOR = {
  "Regular":            { bg: "var(--green-soft)", border: "#a8c98b", color: "#345E15" },
  "Visiting Local":     { bg: "var(--gold-soft)",  border: "#dec98e", color: "#6A5310" },
  "Visiting Non-Local": { bg: "var(--rose-soft)",  border: "#e9b6a6", color: "#7C2E1C" },
  "Consultant Recruited":{bg: "var(--teal-soft)",  border: "#9fd2cc", color: "var(--teal-ink)" },
  "Unclassified":       { bg: "#F5F2EC",           border: "#E4DFD6", color: "var(--text-3)" },
};

function StatBar({ label, value, total, color }) {
  const pct = total ? Math.round(value / total * 100) : 0;
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3}}>
        <span>{label}</span>
        <span className="mono" style={{fontWeight:500}}>{value}/{total} ({pct}%)</span>
      </div>
      <div style={{height:6, borderRadius:3, background:'var(--border)', overflow:'hidden'}}>
        <div style={{height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width 0.5s ease'}}/>
      </div>
    </div>
  );
}

function Body({ onLock }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/stats/inspection").then(({data}) => { if (alive) setData(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data) return <div style={{padding:40, textAlign:'center'}}><div className="spinner"/></div>;

  const { type_counts, department_risk, over_70, totals, compliance, designation_distribution } = data;
  const ta = totals.active;

  return (
    <div data-testid="inspection-body">
      <div className="insp-banner" style={{display:'flex', alignItems:'center', gap:14}}>
        <div style={{flex:1}}>
          <h3>&#9873; Inspection Readiness &middot; Confidential</h3>
          <p>Employment types visible &middot; For authorised admins only. Lock when done.</p>
        </div>
        <button className="btn gold" onClick={onLock} data-testid="inspection-lock">Lock</button>
      </div>

      {/* Totals row */}
      <div className="grid grid-4" style={{marginBottom:14}}>
        <div className="kpi-card" style={{borderColor:'#a8c98b'}} data-testid="kpi-active">
          <div className="kpi-label">Active Faculty</div>
          <div className="kpi-value" style={{color:'#345E15'}}>{ta}</div>
          <div className="kpi-foot">across all departments</div>
        </div>
        <div className="kpi-card" style={{borderColor:'#e9b6a6'}} data-testid="kpi-absconded">
          <div className="kpi-label">Absconded</div>
          <div className="kpi-value" style={{color:'#7C2E1C'}}>{totals.absconded}</div>
          <div className="kpi-foot">pending action</div>
        </div>
        <div className="kpi-card" style={{borderColor:'#E4DFD6'}} data-testid="kpi-relieved">
          <div className="kpi-label">Relieved / Archived</div>
          <div className="kpi-value" style={{color:'var(--text-3)'}}>{totals.relieved + (totals.archived || 0)}</div>
          <div className="kpi-foot">historical</div>
        </div>
        <div className="kpi-card" style={{borderColor:'#dec98e'}} data-testid="kpi-total">
          <div className="kpi-label">Total Records</div>
          <div className="kpi-value" style={{color:'#6A5310'}}>{totals.total}</div>
          <div className="kpi-foot">in system</div>
        </div>
      </div>

      {/* Employment-type KPIs */}
      <div style={{fontSize:10, textTransform:'uppercase', letterSpacing:1, color:'var(--text-3)', fontWeight:600, marginBottom:8}}>Employment Classification</div>
      <div className="grid grid-4" style={{marginBottom:8}}>
        {["Regular","Visiting Local","Visiting Non-Local","Consultant Recruited"].map(t => {
          const c = TYPE_COLOR[t];
          return (
            <div className="kpi-card" key={t} style={{borderColor: c.border}} data-testid={`emp-kpi-${t.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="kpi-label" style={{color: c.color}}>{t.toUpperCase()}</div>
              <div className="kpi-value" style={{color: c.color}}>{type_counts[t] || 0}</div>
              <div className="kpi-foot">faculty</div>
              <div style={{position:'absolute', top:0, left:0, right:0, height:3, background:c.color}}/>
            </div>
          );
        })}
      </div>
      {type_counts["Unclassified"] > 0 && (
        <div style={{padding:'8px 14px', borderRadius:8, background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.3)', fontSize:12, color:'#b45309', marginBottom:18}}>
          <strong>{type_counts["Unclassified"]}</strong> faculty have no employment type set. Use Edit or Bulk CSV Import to classify them before an inspection.
        </div>
      )}

      {/* NMC Compliance & Data Completeness */}
      <div className="grid grid-2" style={{marginBottom:18}}>
        <div className="card">
          <div className="card-title">NMC Compliance Readiness</div>
          <div style={{display:'flex', gap:16, marginBottom:14, flexWrap:'wrap'}}>
            <div style={{textAlign:'center', padding:'10px 16px', borderRadius:8, background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.2)', flex:1}}>
              <div className="mono" style={{fontSize:22, fontWeight:700, color:'#059669'}}>{compliance.compliant}</div>
              <div style={{fontSize:11, color:'var(--text-2)'}}>Compliant</div>
            </div>
            <div style={{textAlign:'center', padding:'10px 16px', borderRadius:8, background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.3)', flex:1}}>
              <div className="mono" style={{fontSize:22, fontWeight:700, color:'#b45309'}}>{compliance.compliant_warn}</div>
              <div style={{fontSize:11, color:'var(--text-2)'}}>With Warnings</div>
            </div>
            <div style={{textAlign:'center', padding:'10px 16px', borderRadius:8, background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)', flex:1}}>
              <div className="mono" style={{fontSize:22, fontWeight:700, color:'#dc2626'}}>{compliance.non_compliant}</div>
              <div style={{fontSize:11, color:'var(--text-2)'}}>Non-Compliant</div>
            </div>
            <div style={{textAlign:'center', padding:'10px 16px', borderRadius:8, background:'rgba(0,0,0,0.03)', border:'1px solid var(--border)', flex:1}}>
              <div className="mono" style={{fontSize:22, fontWeight:700, color:'var(--text-3)'}}>{compliance.not_reviewed}</div>
              <div style={{fontSize:11, color:'var(--text-2)'}}>Not Reviewed</div>
            </div>
          </div>
          <StatBar label="MET Course Completed" value={compliance.met_done} total={ta} color="#059669"/>
          <StatBar label="BMR Course Completed" value={compliance.bmr_done} total={ta} color="#059669"/>
          <StatBar label="PG Qualification on File" value={compliance.has_pg} total={ta} color="#2563eb"/>
          <StatBar label="UG Registration on File" value={compliance.has_ug_reg} total={ta} color="#2563eb"/>
          <StatBar label="PG Registration on File" value={compliance.has_pg_reg} total={ta} color="#2563eb"/>
        </div>

        <div className="card">
          <div className="card-title">Data Completeness</div>
          <StatBar label="Aadhaar Verified" value={compliance.has_aadhaar} total={ta} color="#059669"/>
          <StatBar label="PAN on File" value={compliance.has_pan} total={ta} color="#059669"/>
          <StatBar label="Bank Details Complete" value={compliance.has_bank} total={ta} color="#2563eb"/>
          <div style={{borderTop:'1px solid var(--border)', paddingTop:14, marginTop:14}}>
            <div style={{fontSize:10, textTransform:'uppercase', letterSpacing:1, color:'var(--text-3)', fontWeight:600, marginBottom:10}}>Designation Distribution</div>
            {Object.entries(designation_distribution).sort((a,b) => b[1]-a[1]).map(([d, c]) => (
              <div key={d} style={{display:'flex', justifyContent:'space-between', fontSize:12, padding:'3px 0', borderBottom:'1px solid var(--border)'}}>
                <span>{d}</span>
                <span className="mono" style={{fontWeight:500}}>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Department Risk Table */}
      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">Department Strength &amp; Risk</div>
          <table className="data">
            <thead>
              <tr>
                <th>Department</th>
                <th style={{textAlign:'center'}}>Actual</th>
                <th style={{textAlign:'center'}}>NMC Req.</th>
                <th style={{textAlign:'center'}}>Regular</th>
                <th>Dependency</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody data-testid="dept-risk-tbody">
              {department_risk.map(r => (
                <tr key={r.department} style={r.total === 0 ? {opacity:0.5} : null}>
                  <td><span className="dept-name">{r.department}</span><div className="dept-sub">{r.type}</div></td>
                  <td className="num" style={{color: r.total < r.required ? '#dc2626' : '#059669', fontWeight:600}}>{r.total}</td>
                  <td className="num">{r.required}</td>
                  <td className="num">{r.regular}</td>
                  <td>
                    {r.total === 0 ? (
                      <span style={{fontSize:11, color:'var(--text-3)', fontStyle:'italic'}}>vacant</span>
                    ) : (
                      <>
                        <div className="bar-sm"><span style={{width:`${r.dependency_pct}%`, background: r.risk === 'High' ? 'var(--bad)' : r.risk === 'Medium' ? 'var(--warn)' : 'var(--ok)'}}/></div>
                        <span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--text-2)'}}>{r.dependency_pct}%</span>
                      </>
                    )}
                  </td>
                  <td>{r.total === 0 ? <span style={{fontSize:11, color:'var(--text-3)'}}>&mdash;</span> : <span className={`badge ${r.risk === 'High' ? 'bad' : r.risk === 'Medium' ? 'warn' : 'ok'}`}>{r.risk}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-title">Over-70 Retention List</div>
          {over_70.length === 0 && <div style={{color:'var(--text-3)', fontSize:13, padding:'10px 0'}}>No faculty currently aged 70 or above.</div>}
          {over_70.map(f => (
            <div className="act-item" key={f.faculty_id} style={{cursor:'pointer'}} onClick={() => navigate(`/profile/${f.faculty_id}`)} data-testid={`over70-${f.ssmch_id}`}>
              <div className="act-dot" style={{background: f.aebas_present ? 'var(--warn)' : 'var(--bad)'}}/>
              <div>
                <strong>Dr. {f.full_name}</strong> &middot; {f.designation}, {f.department}
                <div style={{fontSize:11, color:'var(--text-3)', marginTop:2}}>
                  Age {f.age} &middot; AEBAS {f.aebas_present ? 'present (anomaly)' : 'absent (expected)'} &middot; {f.employment_type}
                </div>
              </div>
              <div className="act-time">{f.ssmch_id}</div>
            </div>
          ))}

          {/* Inspection checklist */}
          <div style={{borderTop:'1px solid var(--border)', paddingTop:14, marginTop:14}}>
            <div style={{fontSize:10, textTransform:'uppercase', letterSpacing:1, color:'var(--text-3)', fontWeight:600, marginBottom:10}}>Pre-Inspection Checklist</div>
            {[
              { label: "All faculty have employment type classified", ok: (type_counts["Unclassified"] || 0) === 0 },
              { label: "MET completion rate above 80%", ok: compliance.met_rate >= 80 },
              { label: "BMR completion rate above 80%", ok: compliance.bmr_rate >= 80 },
              { label: "All active faculty have Aadhaar on file", ok: compliance.has_aadhaar >= ta },
              { label: "All active faculty have bank details", ok: compliance.has_bank >= ta },
              { label: "No non-compliant faculty in system", ok: compliance.non_compliant === 0 },
              { label: "No faculty aged 70+", ok: over_70.length === 0 },
              { label: "All departments above minimum strength", ok: department_risk.every(d => d.total === 0 || d.total >= d.required) },
            ].map((item, i) => (
              <div key={i} style={{display:'flex', gap:8, alignItems:'center', fontSize:12, padding:'5px 0', borderBottom:'1px solid var(--border)'}}>
                <span style={{fontSize:14, color: item.ok ? '#059669' : '#dc2626'}}>{item.ok ? '\u2713' : '\u2717'}</span>
                <span style={{color: item.ok ? 'var(--text-1)' : '#dc2626'}}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Inspection() {
  const { inspectionUnlocked, lockInspection } = useAuth();
  const [open, setOpen] = useState(inspectionUnlocked);

  const handleLock = () => { lockInspection(); setOpen(false); };

  if (!open) {
    return (
      <div data-testid="view-inspection">
        <div className="topbar">
          <div>
            <h1 className="pg-title">Inspection Readiness</h1>
            <div className="pg-sub">Confidential layer &middot; authorised personnel only</div>
          </div>
        </div>
        <Gate onSuccess={() => setOpen(true)}/>
      </div>
    );
  }

  return (
    <div data-testid="view-inspection">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Inspection Readiness</h1>
          <div className="pg-sub">Confidential layer &middot; authorised personnel only</div>
        </div>
        <div className="topbar-meta"><span className="meta-chip" style={{color:'var(--bad)', borderColor:'#e9b6a6', background:'var(--rose-soft)'}}>&#9679; Unlocked</span></div>
      </div>
      <Body onLock={handleLock}/>
    </div>
  );
}
